import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  ClipboardList,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  ArrowRight,
  Lightbulb,
  Calendar,
  FileText,
  RotateCcw,
} from "lucide-react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Button } from "@/shared/ui";
import { useCaseSummary } from "../hooks/useCaseSummary";
import type { CaseSummaryParams, CaseSummaryResult } from "../types/case-summary.types";

const PRACTICE_AREAS = [
  { value: "civil", labelKey: "lawyer.caseSummary.practiceAreas.civil" },
  { value: "penal", labelKey: "lawyer.caseSummary.practiceAreas.penal" },
  { value: "laboral", labelKey: "lawyer.caseSummary.practiceAreas.laboral" },
  { value: "comercial", labelKey: "lawyer.caseSummary.practiceAreas.comercial" },
  { value: "fiscal", labelKey: "lawyer.caseSummary.practiceAreas.fiscal" },
  { value: "familia", labelKey: "lawyer.caseSummary.practiceAreas.familia" },
  { value: "administrativo", labelKey: "lawyer.caseSummary.practiceAreas.administrativo" },
  { value: "inmobiliario", labelKey: "lawyer.caseSummary.practiceAreas.inmobiliario" },
];

const PRIORITIES = [
  { value: "low", labelKey: "lawyer.caseSummary.priorities.low" },
  { value: "medium", labelKey: "lawyer.caseSummary.priorities.medium" },
  { value: "high", labelKey: "lawyer.caseSummary.priorities.high" },
  { value: "urgent", labelKey: "lawyer.caseSummary.priorities.urgent" },
];

const STATUSES = [
  { value: "active", labelKey: "lawyer.caseSummary.statuses.active" },
  { value: "pending", labelKey: "lawyer.caseSummary.statuses.pending" },
  { value: "in_review", labelKey: "lawyer.caseSummary.statuses.in_review" },
  { value: "closed", labelKey: "lawyer.caseSummary.statuses.closed" },
];

const fieldClass =
  "w-full px-4 py-2.5 border border-romance-200 rounded-lg bg-white text-juridico focus:ring-2 focus:ring-tiber-500 focus:border-tiber-500 focus:outline-none transition-colors text-sm";
const labelClass = "block text-sm font-medium text-juridico mb-1.5";

export const CaseSummaryPage: React.FC = () => {
  const { t } = useTranslation();
  const { generateSummary, loading, error, result, clearResult } = useCaseSummary();

  const [formData, setFormData] = useState<CaseSummaryParams>({
    caseTitle: "",
    caseDescription: "",
    caseNumber: "",
    practiceArea: "civil",
    status: "active",
    priority: "medium",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.caseTitle.trim() || !formData.caseNumber.trim()) return;
    await generateSummary(formData);
  };

  const handleNewSummary = () => {
    clearResult();
    setFormData({
      caseTitle: "",
      caseDescription: "",
      caseNumber: "",
      practiceArea: "civil",
      status: "active",
      priority: "medium",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-tiber-50 rounded-xl border border-tiber-100">
            <ClipboardList className="w-7 h-7 text-tiber-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-display font-bold text-juridico">
                {t("lawyer.caseSummary.title")}
              </h1>
              <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-tiber-50 border border-tiber-200 rounded-full text-xs font-medium text-tiber-700">
                <Sparkles className="w-3 h-3" />
                IA Legal
              </span>
            </div>
            <p className="text-juridico/60 mt-0.5 text-sm">{t("lawyer.caseSummary.subtitle")}</p>
          </div>
        </div>
      </div>

      {/* AI Disclaimer */}
      <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800">
          {t("lawyer.caseSummary.disclaimer")}
        </p>
      </div>

      {result ? (
        <SummaryResultView result={result} onNewSummary={handleNewSummary} />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg border border-romance-200 p-6"
        >
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Case Number + Practice Area */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>
                  {t("lawyer.caseSummary.form.caseNumber")}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <input
                  type="text"
                  value={formData.caseNumber}
                  onChange={(e) => setFormData({ ...formData, caseNumber: e.target.value })}
                  placeholder="EXP-2024-001"
                  className={fieldClass}
                  required
                />
              </div>
              <div>
                <label className={labelClass}>
                  {t("lawyer.caseSummary.form.practiceArea")}
                </label>
                <select
                  value={formData.practiceArea}
                  onChange={(e) => setFormData({ ...formData, practiceArea: e.target.value })}
                  className={fieldClass}
                >
                  {PRACTICE_AREAS.map((area) => (
                    <option key={area.value} value={area.value}>
                      {t(area.labelKey)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Case Title */}
            <div>
              <label className={labelClass}>
                {t("lawyer.caseSummary.form.title")}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                value={formData.caseTitle}
                onChange={(e) => setFormData({ ...formData, caseTitle: e.target.value })}
                placeholder={t("lawyer.caseSummary.form.titlePlaceholder")}
                className={fieldClass}
                required
              />
            </div>

            {/* Status + Priority */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t("lawyer.caseSummary.form.status")}</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className={fieldClass}
                >
                  {STATUSES.map((s) => (
                    <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>{t("lawyer.caseSummary.form.priority")}</label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className={fieldClass}
                >
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>{t(p.labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className={labelClass}>
                {t("lawyer.caseSummary.form.description")}
                <span className="text-juridico/40 font-normal ml-1 text-xs">
                  ({t("common.optional")})
                </span>
              </label>
              <textarea
                value={formData.caseDescription || ""}
                onChange={(e) => setFormData({ ...formData, caseDescription: e.target.value })}
                placeholder={t("lawyer.caseSummary.form.descriptionPlaceholder")}
                rows={6}
                className={`${fieldClass} resize-none`}
              />
              <p className="text-xs text-juridico/40 mt-1">
                {t("lawyer.caseSummary.form.descriptionHint")}
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || !formData.caseTitle.trim() || !formData.caseNumber.trim()}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("lawyer.caseSummary.form.generating")}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t("lawyer.caseSummary.form.submit")}
                </>
              )}
            </Button>
          </form>
        </motion.div>
      )}
    </div>
  );
};

const SummaryResultView: React.FC<{
  result: CaseSummaryResult;
  onNewSummary: () => void;
}> = ({ result, onNewSummary }) => {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-4"
    >
      {/* Executive Summary */}
      <div className="bg-white rounded-lg border border-romance-200 p-5">
        <h3 className="text-base font-semibold text-juridico mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5 text-tiber-600" />
          {t("lawyer.caseSummary.result.summary")}
        </h3>
        <p className="text-sm text-juridico/80 whitespace-pre-line leading-relaxed">{result.summary}</p>
      </div>

      {/* Key Points */}
      <div className="bg-white rounded-lg border border-romance-200 p-5">
        <h3 className="text-base font-semibold text-juridico mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-atlantis-600" />
          {t("lawyer.caseSummary.result.keyPoints")}
        </h3>
        <ul className="space-y-2">
          {result.keyPoints.map((point, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-juridico/80">
              <span className="w-1.5 h-1.5 rounded-full bg-tiber-500 mt-1.5 shrink-0" />
              {point}
            </li>
          ))}
        </ul>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-lg border border-romance-200 p-5">
        <h3 className="text-base font-semibold text-juridico mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-tiber-600" />
          {t("lawyer.caseSummary.result.timeline")}
        </h3>
        <p className="text-sm text-juridico/80 leading-relaxed">{result.timeline}</p>
      </div>

      {/* Next Steps */}
      <div className="bg-white rounded-lg border border-romance-200 p-5">
        <h3 className="text-base font-semibold text-juridico mb-3 flex items-center gap-2">
          <ArrowRight className="w-5 h-5 text-tiber-600" />
          {t("lawyer.caseSummary.result.nextSteps")}
        </h3>
        <ul className="space-y-2.5">
          {result.nextSteps.map((step, idx) => (
            <li key={idx} className="flex items-start gap-3 text-sm text-juridico/80">
              <span className="shrink-0 w-5 h-5 bg-tiber-50 border border-tiber-200 text-tiber-700 rounded-full flex items-center justify-center text-xs font-semibold">
                {idx + 1}
              </span>
              {step}
            </li>
          ))}
        </ul>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-lg border border-romance-200 p-5">
        <h3 className="text-base font-semibold text-juridico mb-3 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-600" />
          {t("lawyer.caseSummary.result.recommendations")}
        </h3>
        <ul className="space-y-2">
          {result.recommendations.map((rec, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-juridico/80">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
              {rec}
            </li>
          ))}
        </ul>
      </div>

      {/* Action */}
      <button
        onClick={onNewSummary}
        className="flex items-center gap-2 text-sm font-medium text-tiber-600 hover:text-tiber-700 transition-colors"
      >
        <RotateCcw className="w-4 h-4" />
        {t("lawyer.caseSummary.result.newSummary")}
      </button>
    </motion.div>
  );
};

export default CaseSummaryPage;
